/** @format */

import {Box, Skeleton} from '@mui/material';
import {Trans} from 'react-i18next';

import theme from 'theme';

import {WIDGETS_LIST} from 'pages/Home/DigitalCast/constants/widgets';
import {ChartIds} from 'pages/Home/DigitalCast/models/chartIds';

import MainChart from 'components/charts/multiline-chart/components/main-chart';

import reworkTooltipData from 'pages/Home/DigitalCast/components/dashboard/helpers/reworkTooltipData';

import {CHARTS_MARGIN_BOTTOM, CHARTS_MARGIN_TOP} from 'components/charts/multiline-chart/const';

import {MultilineChartInterface} from 'components/charts/multiline-chart/model/common';

import {GraphComplexValuesList} from 'pages/Home/DigitalCast/models/common';

import {WidgetConditionsList} from 'pages/Home/DigitalCast/models/spaces';

import {Chart, CommonChartData} from 'components/charts/multiline-chart/model/charts';

import {StatState} from 'types';

import SlashLoader from 'components/loading/SlashLoader';

import {CHART_HEIGHT, MAX_FORECAST_POINTS} from '../../constants';
import {PlanFact} from '../PlanFact';
import {ChartContainer, ChartWrapper, LabelText, LoaderWrapper, WidgetContainer} from './styles';

export interface KpiAndIndicatorsProps {
  chartId: ChartIds;
  charts: MultilineChartInterface<ChartIds>;
  widgetConditions: WidgetConditionsList;
  graphComplexValues: GraphComplexValuesList;
  todayPointName: string;
  tokenStatus?: StatState | null;
  tailDays?: number;
}

const Widget = ({
  charts,
  chartId,
  widgetConditions,
  graphComplexValues,
  todayPointName,
  tokenStatus,
  tailDays = 0,
}: KpiAndIndicatorsProps) => {
  const chart = charts.charts.find(item => item.chartId === chartId);
  const condition = widgetConditions[chartId];

  if (!chart?.chartsData.length || !condition) {
    return (
      <Skeleton
        data-testid='skeleton'
        variant='rectangular'
        height={CHART_HEIGHT}
        sx={{padding: theme.spacing(8), borderRadius: theme.spacing(2)}}
      />
    );
  }

  // Trim excess forecast points to ensure the chart displays only the most relevant data.
  // Forecast points are identified by the presence of average or averageSecondary values.
  // The maximum number of forecast points to display is controlled by MAX_FORECAST_POINTS.

  const chartsData: CommonChartData[] = [];
  let forecastPointsCount = 0;
  for (let i = 0; i < chart.chartsData.length; i++) {
    const item = chart.chartsData[i];
    const isForecast = Boolean(item.average !== undefined || item.averageSecondary !== undefined);
    if (isForecast) {
      if (forecastPointsCount > MAX_FORECAST_POINTS) {
        break;
      }
      forecastPointsCount++;
    }
    chartsData.push(item);
  }

  const newChartData: Chart<ChartIds> = {
    ...chart,
    chartsData,
    color: '#547AE0',
  };

  const {tooltipDataList} = reworkTooltipData(charts, [chartId]);

  const chartForPlanFact: Chart<ChartIds> =
    tailDays > 0 ? {...chart, chartsData: chart.chartsData.slice(tailDays)} : chart;

  const {label} = WIDGETS_LIST[chartId];

  const content = () => {
    if (tokenStatus === 'NOT_READY') {
      return (
        <LoaderWrapper>
          <SlashLoader fullScreen={false} />
        </LoaderWrapper>
      );
    }

    return (
      <MainChart
        key={chartId}
        syncId={chartId}
        tooltipData={tooltipDataList}
        onChartClick={() => {}}
        chart={newChartData}
        chartIndex={0}
        chartTypeSettings={{[chartId]: 'primary'}}
        overrideChartHeight={CHART_HEIGHT}
        margin={{
          top: CHARTS_MARGIN_TOP,
          bottom: CHARTS_MARGIN_BOTTOM,
        }}
        maxPitLabels={1}
        maxPeakLabels={1}
        isCompactTooltip
      />
    );
  };

  return (
    <WidgetContainer>
      <Box>
        <LabelText>
          <Trans>{label}</Trans>
        </LabelText>
        <PlanFact
          chart={chartForPlanFact}
          todayPointName={todayPointName}
          graphComplexValuesList={graphComplexValues}
          isPlanUnreachable={condition === 'unreachable'}
        />
      </Box>
      <ChartContainer>
        <ChartWrapper>{content()}</ChartWrapper>
      </ChartContainer>
    </WidgetContainer>
  );
};

export default Widget;
